import React, { useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable, DataTablePageEvent } from 'primereact/datatable';
import { Tag } from 'primereact/tag';
import { LecturerPaymentRateListItem } from '../../../../api/types/lecturers/price-rate/lecturer-payment-rate-list-item';
import { LecturerRateDisplayType } from '../../../../api/types/lecturers/price-rate/lecturer-rate-display-type';
import { RequestRateStatus } from '../../../../api/types/lecturers/price-rate/request-rate/request-rate-status';
import { getPaymentTypeDisplayText } from '../../../../api/types/settings/lecturers/settings-lecturers-rate-type';
import { LegalEntityType } from '../../../user-page/roles/lecturer-price-rate/componenets/acountant/user-page-lecturer-rate-accountant';
import { formatDate } from '../../../../utils/date-format';
import { RatesReportColumnPicker } from './components/rates-report-column-picker';
import { RatesReportFilter } from './components/rates-report-filter';
import { exportRatesReportToExcelOperation } from './operations/export-rates-report-to-excel-operation';
import { getRatesReportListOperation } from './operations/get-rates-report-list-operation';
import {
  defaultRatesReportFilter,
  RatesReportFilterType,
} from './rates-report-filter.type';

type ColumnKey =
  | 'lecturerName'
  | 'nationalId'
  | 'branchName'
  | 'activityName'
  | 'rate'
  | 'travelRate'
  | 'displayType'
  | 'startTime'
  | 'endTime'
  | 'requestStatus'
  | 'paymentType'
  | 'requestDescription'
  | 'legalEntityType'
  | 'reportingCode'
  | 'legalEntityId'
  | 'accountingReportCodeTravel'
  | 'contractDoc'
  | 'signedContractDoc'
  | 'createdAt'
  | 'createdByName'
  | 'statusChangedAt'
  | 'statusChangedName';

const DEFAULT_VISIBLE_COLUMNS: ColumnKey[] = [
  'lecturerName',
  'nationalId',
  'branchName',
  'activityName',
  'rate',
  'travelRate',
  'displayType',
  'startTime',
  'endTime',
  'requestStatus',
];

const COLUMN_OPTIONS: { label: string; value: ColumnKey }[] = [
  { label: 'מרצה', value: 'lecturerName' },
  { label: 'ת.ז.', value: 'nationalId' },
  { label: 'שלוחה', value: 'branchName' },
  { label: 'פעילות', value: 'activityName' },
  { label: 'תעריף', value: 'rate' },
  { label: 'תעריף נסיעות', value: 'travelRate' },
  { label: 'סוג תצוגה', value: 'displayType' },
  { label: 'תוקף מ-', value: 'startTime' },
  { label: 'תוקף עד', value: 'endTime' },
  { label: 'סטטוס', value: 'requestStatus' },
  { label: 'סוג תשלום', value: 'paymentType' },
  { label: 'תיאור בקשה', value: 'requestDescription' },
  { label: 'סוג ישות', value: 'legalEntityType' },
  { label: 'קוד דיווח', value: 'reportingCode' },
  { label: 'מזהה ישות', value: 'legalEntityId' },
  { label: 'קוד דיווח נסיעות', value: 'accountingReportCodeTravel' },
  { label: 'חוזה', value: 'contractDoc' },
  { label: 'חוזה חתום', value: 'signedContractDoc' },
  { label: 'נוצר ב-', value: 'createdAt' },
  { label: 'נוצר ע״י', value: 'createdByName' },
  { label: 'סטטוס עודכן ב-', value: 'statusChangedAt' },
  { label: 'סטטוס עודכן ע״י', value: 'statusChangedName' },
];

const displayTypeLabel = (displayType: LecturerRateDisplayType): string => {
  switch (displayType) {
    case LecturerRateDisplayType.OneHour:
      return 'שעה שלמה';
    case LecturerRateDisplayType.AcademicHour:
      return 'שעה אקדמית';
    default:
      return displayType;
  }
};

const requestStatusLabel = (status: RequestRateStatus): string => {
  switch (status) {
    case RequestRateStatus.Approved:
      return 'מאושר';
    case RequestRateStatus.Pending:
      return 'ממתין';
    case RequestRateStatus.Denied:
      return 'נדחה';
    default:
      return status;
  }
};

const requestStatusSeverity = (
  status: RequestRateStatus,
): 'success' | 'warning' | 'danger' | 'info' => {
  switch (status) {
    case RequestRateStatus.Approved:
      return 'success';
    case RequestRateStatus.Pending:
      return 'warning';
    case RequestRateStatus.Denied:
      return 'danger';
    default:
      return 'info';
  }
};

const formatMoney = (value: number | null | undefined): string => {
  if (value == null) return '—';
  return `₪${value.toLocaleString()}`;
};

const formatNullableDate = (
  value: Date | string | null | undefined,
): string => {
  if (value == null) return '—';
  return formatDate(new Date(value));
};

const cellTextAlign = { textAlign: 'right' as const };

export const RatesReport = () => {
  const [data, setData] = useState<LecturerPaymentRateListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(20);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState<RatesReportFilterType>(
    defaultRatesReportFilter(),
  );
  const [appliedFilters, setAppliedFilters] =
    useState<RatesReportFilterType>(filters);

  const [visibleColumns, setVisibleColumns] = useState<ColumnKey[]>(
    DEFAULT_VISIBLE_COLUMNS,
  );
  const [exportingExcel, setExportingExcel] = useState(false);

  const searchSeqRef = useRef(0);

  const isColumnVisible = (key: ColumnKey) => visibleColumns.includes(key);

  const runSearch = async (
    nextFilters: RatesReportFilterType,
    nextOffset: number,
    nextLimit: number,
  ) => {
    const seq = ++searchSeqRef.current;
    const isCurrent = () => seq === searchSeqRef.current;

    await getRatesReportListOperation(
      nextFilters,
      nextOffset,
      nextLimit,
      (next) => {
        if (isCurrent()) setData(next);
      },
      (next) => {
        if (isCurrent()) setTotal(next);
      },
      (next) => {
        if (isCurrent()) setLoading(next);
      },
    );
  };

  const handleSearch = async () => {
    setAppliedFilters(filters);
    setOffset(0);
    setData([]);
    setTotal(0);
    await runSearch(filters, 0, limit);
  };

  const handleClear = () => {
    setFilters(defaultRatesReportFilter());
  };

  const onPageChange = async (event: DataTablePageEvent) => {
    const newOffset = event.first;
    const newLimit = event.rows;
    setOffset(newOffset);
    setLimit(newLimit);
    await runSearch(appliedFilters, newOffset, newLimit);
  };

  const handleExportExcel = () => {
    if (data.length === 0 || exportingExcel) return;
    exportRatesReportToExcelOperation(data, setExportingExcel);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '16px',
        direction: 'rtl',
      }}
    >
      <RatesReportFilter
        filters={filters}
        loading={loading}
        onChange={setFilters}
        onSearch={handleSearch}
        onClear={handleClear}
      />

      <div
        style={{
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
          padding: '12px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            fontSize: '14px',
            color: '#4b5563',
          }}
        >
          <span style={{ textAlign: 'right' }}>
            סה״כ רשומות: {total.toLocaleString()}
          </span>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              minWidth: '330px',
            }}
          >
            <span style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
              עמודות
            </span>
            <RatesReportColumnPicker
              options={COLUMN_OPTIONS}
              value={visibleColumns}
              defaultValue={DEFAULT_VISIBLE_COLUMNS}
              onChange={setVisibleColumns}
            />

            <Button
              icon="pi pi-file-excel"
              tooltip={
                data.length > 0
                  ? `ייצוא ${data.length} רשומות לאקסל`
                  : 'אין נתונים לייצוא'
              }
              tooltipOptions={{ position: 'top', showOnDisabled: true }}
              onClick={handleExportExcel}
              loading={exportingExcel}
              disabled={data.length === 0 || exportingExcel}
              style={{
                backgroundColor: '#10b981',
                borderColor: '#10b981',
                height: '32px',
              }}
            />
          </div>
        </div>

        <DataTable
          value={data}
          rowHover
          loading={loading}
          paginator
          rows={limit}
          first={offset}
          totalRecords={total}
          onPage={onPageChange}
          lazy={true}
          rowsPerPageOptions={[10, 20, 50, 100]}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          emptyMessage={
            <div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>
          }
          dataKey="id"
          tableStyle={{ direction: 'rtl' }}
          scrollable
          scrollHeight="calc(100vh - 320px)"
        >
          {isColumnVisible('lecturerName') && (
            <Column
              header="מרצה"
              body={(row: LecturerPaymentRateListItem) =>
                `${row.firstName} ${row.lastName}`
              }
              style={cellTextAlign}
              headerStyle={cellTextAlign}
            />
          )}
          {isColumnVisible('nationalId') && (
            <Column
              field="nationalId"
              header="ת.ז."
              style={cellTextAlign}
              headerStyle={cellTextAlign}
            />
          )}
          {isColumnVisible('branchName') && (
            <Column
              field="branchName"
              header="שלוחה"
              style={cellTextAlign}
              headerStyle={cellTextAlign}
            />
          )}
          {isColumnVisible('activityName') && (
            <Column
              field="activityName"
              header="פעילות"
              style={cellTextAlign}
              headerStyle={cellTextAlign}
            />
          )}
          {isColumnVisible('rate') && (
            <Column
              header="תעריף"
              body={(row: LecturerPaymentRateListItem) => formatMoney(row.rate)}
              style={cellTextAlign}
              headerStyle={cellTextAlign}
            />
          )}
          {isColumnVisible('travelRate') && (
            <Column
              header="תעריף נסיעות"
              body={(row: LecturerPaymentRateListItem) =>
                formatMoney(row.travelRate)
              }
              style={cellTextAlign}
              headerStyle={cellTextAlign}
            />
          )}
          {isColumnVisible('displayType') && (
            <Column
              header="סוג תצוגה"
              body={(row: LecturerPaymentRateListItem) =>
                displayTypeLabel(row.displayType)
              }
              style={cellTextAlign}
              headerStyle={cellTextAlign}
            />
          )}
          {isColumnVisible('startTime') && (
            <Column
              header="תוקף מ-"
              body={(row: LecturerPaymentRateListItem) =>
                formatNullableDate(row.startTime)
              }
              style={cellTextAlign}
              headerStyle={cellTextAlign}
            />
          )}
          {isColumnVisible('endTime') && (
            <Column
              header="תוקף עד"
              body={(row: LecturerPaymentRateListItem) =>
                formatNullableDate(row.endTime)
              }
              style={cellTextAlign}
              headerStyle={cellTextAlign}
            />
          )}
          {isColumnVisible('requestStatus') && (
            <Column
              header="סטטוס"
              body={(row: LecturerPaymentRateListItem) => (
                <Tag
                  value={requestStatusLabel(row.requestStatus)}
                  severity={requestStatusSeverity(row.requestStatus)}
                />
              )}
              style={cellTextAlign}
              headerStyle={cellTextAlign}
            />
          )}
          {isColumnVisible('paymentType') && (
            <Column
              header="סוג תשלום"
              body={(row: LecturerPaymentRateListItem) =>
                getPaymentTypeDisplayText(row.paymentType)
              }
              style={cellTextAlign}
              headerStyle={cellTextAlign}
            />
          )}
          {isColumnVisible('requestDescription') && (
            <Column
              field="requestDescription"
              header="תיאור בקשה"
              style={cellTextAlign}
              headerStyle={cellTextAlign}
            />
          )}
          {isColumnVisible('legalEntityType') && (
            <Column
              header="סוג ישות"
              body={(row: LecturerPaymentRateListItem) =>
                row.legalEntityType
                  ? LegalEntityType.getDisplayText(row.legalEntityType)
                  : '—'
              }
              style={cellTextAlign}
              headerStyle={cellTextAlign}
            />
          )}
          {isColumnVisible('reportingCode') && (
            <Column
              header="קוד דיווח"
              body={(row: LecturerPaymentRateListItem) =>
                row.reportingCode ?? '—'
              }
              style={cellTextAlign}
              headerStyle={cellTextAlign}
            />
          )}
          {isColumnVisible('legalEntityId') && (
            <Column
              header="מזהה ישות"
              body={(row: LecturerPaymentRateListItem) =>
                row.legalEntityId ?? '—'
              }
              style={cellTextAlign}
              headerStyle={cellTextAlign}
            />
          )}
          {isColumnVisible('accountingReportCodeTravel') && (
            <Column
              header="קוד דיווח נסיעות"
              body={(row: LecturerPaymentRateListItem) =>
                row.accountingReportCodeTravel ?? '—'
              }
              style={cellTextAlign}
              headerStyle={cellTextAlign}
            />
          )}
          {isColumnVisible('contractDoc') && (
            <Column
              header="חוזה"
              body={(row: LecturerPaymentRateListItem) => {
                if (row.contractFileUrl) {
                  return (
                    <a
                      href={row.contractFileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: '#2563eb',
                        textDecoration: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      <i className="pi pi-external-link" />
                      {row.contractDoc?.docName ?? 'חוזה'}
                    </a>
                  );
                }
                return row.contractDoc?.docName ?? '—';
              }}
              style={cellTextAlign}
              headerStyle={cellTextAlign}
            />
          )}
          {isColumnVisible('signedContractDoc') && (
            <Column
              header="חוזה חתום"
              body={(row: LecturerPaymentRateListItem) => {
                if (row.signedContractFileUrl) {
                  return (
                    <a
                      href={row.signedContractFileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: '#2563eb',
                        textDecoration: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      <i className="pi pi-external-link" />
                      {row.signedContractDoc?.docName ?? 'חוזה חתום'}
                    </a>
                  );
                }
                return row.signedContractDoc?.docName ?? '—';
              }}
              style={cellTextAlign}
              headerStyle={cellTextAlign}
            />
          )}
          {isColumnVisible('createdAt') && (
            <Column
              header="נוצר ב-"
              body={(row: LecturerPaymentRateListItem) =>
                formatNullableDate(row.createdAt)
              }
              style={cellTextAlign}
              headerStyle={cellTextAlign}
            />
          )}
          {isColumnVisible('createdByName') && (
            <Column
              field="createdByName"
              header="נוצר ע״י"
              style={cellTextAlign}
              headerStyle={cellTextAlign}
            />
          )}
          {isColumnVisible('statusChangedAt') && (
            <Column
              header="סטטוס עודכן ב-"
              body={(row: LecturerPaymentRateListItem) =>
                formatNullableDate(row.statusChangedAt)
              }
              style={cellTextAlign}
              headerStyle={cellTextAlign}
            />
          )}
          {isColumnVisible('statusChangedName') && (
            <Column
              header="סטטוס עודכן ע״י"
              body={(row: LecturerPaymentRateListItem) =>
                row.statusChangedName ?? '—'
              }
              style={cellTextAlign}
              headerStyle={cellTextAlign}
            />
          )}
        </DataTable>
      </div>
    </div>
  );
};
